/**
 * Offers to switch organizations when a shared link points at a record living in another
 * account the user belongs to. Renders nothing when switching would not help, so pages can
 * drop it into their not-found branch unconditionally.
 */
import { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ConfirmActionModal } from '@insio/ui-toolkit';
import { GetDeepLinkMembershipDocument } from '@/graphql';
import { useSwitchAccount } from '../hooks/use-switch-account';

interface CrossAccountLinkPromptProps {
  /** Node ID from the route — the record the user tried to open. */
  nodeId: string | undefined;
}

export function CrossAccountLinkPrompt({ nodeId }: CrossAccountLinkPromptProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { switchAccount, switching } = useSwitchAccount();
  const [dismissed, setDismissed] = useState(false);

  const { data } = useQuery(GetDeepLinkMembershipDocument, {
    variables: { id: nodeId ?? '' },
    skip: !nodeId
  });

  const membership = data?.deepLinkMembership;
  if (!membership || dismissed) return null;

  return (
    <ConfirmActionModal
      title={t('features.auth.crossAccountLink.title')}
      message={t('features.auth.crossAccountLink.message', {
        name: membership.account.name
      })}
      confirmLabel={t('features.auth.crossAccountLink.confirm', {
        name: membership.account.name
      })}
      variant='primary'
      loading={switching}
      // The server just confirmed this path belongs to the account being entered, so it is
      // safe to carry across the switch.
      onConfirm={() => switchAccount(membership.account.id, location.pathname + location.search)}
      onClose={() => setDismissed(true)}
      testId='cross-account-link-prompt'
    />
  );
}
